import { useQuery } from '@tanstack/react-query';
import { invokeEdgeFunction } from '@/lib/edge-functions';
import { getApiSeasonYear, getPreviousSeasonYear } from '@/lib/season';
import { useLeagueConfig } from '@/hooks/useLeagueConfig';
import { useSeason } from '@/context/SeasonContext';

interface TeamStanding {
  id?: number;
  name: string;
  logo?: string;
  position?: number;
  points?: number;
  eligible: boolean;
  promoted?: boolean;
}

interface StandingsData {
  season: string;
  tddIneligibleTeams: TeamStanding[];
  tddEligibleTeams: TeamStanding[];
  competitionId?: number;
  competitionName?: string;
  lastUpdated: string;
}

export const useStandings = (season: string) => {
  const { competitionId, competition, config: leagueConfig, hasPromotion } = useLeagueConfig();
  const tddIneligibleCount = leagueConfig.tdd_ineligible_count;

  return useQuery({
    queryKey: ['standings', season, competitionId, tddIneligibleCount],
    enabled: competitionId != null,
    queryFn: async () => {
      console.log(`Fetching ${competition.name} standings for season ${season}`);

      const { data, error } = await invokeEdgeFunction('fetch-standings', {
        body: { season, competitionId, tddIneligibleCount, hasPromotion }
      });

      if (error) {
        console.error(`Error fetching ${competition.name} standings:`, error);
        throw new Error(error.message);
      }

      return data as StandingsData;
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: 3,
    retryDelay: 1000,
  });
};

export const useTDDEligibility = (seasonOverride?: string) => {
  const { leagueActiveSeason } = useSeason();
  const season = seasonOverride ?? getPreviousSeasonYear(leagueActiveSeason);
  const { data: standings, isLoading, error } = useStandings(season);

  const getTDDEligibleTeams = () => {
    if (!standings) return [];
    return standings.tddEligibleTeams;
  };

  const getTDDIneligibleTeams = () => {
    if (!standings) return [];
    return standings.tddIneligibleTeams;
  };

  const isTDDEligible = (teamName: string) => {
    if (!standings) return false;
    return standings.tddEligibleTeams.some(team => team.name === teamName);
  };

  return {
    standings,
    isLoading,
    error,
    getTDDEligibleTeams,
    getTDDIneligibleTeams,
    isTDDEligible
  };
};
